import { useEffect, useState, type FormEvent } from 'react';
import { copyText } from '../../lib/clipboard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Copy, Download, ShieldCheck, Smartphone } from 'lucide-react';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useMe } from '../../hooks/useMe';
import { Badge, Button, Card, CardBody, CardHeader, Field, InlineError, Input, Notice, useToast } from '../ui';

/** Two-step sign-in with an authenticator app (password sign-in only). */
export function TwoFactorCard() {
  const me = useMe().data;
  const qc = useQueryClient();
  const toast = useToast();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [offPassword, setOffPassword] = useState('');
  const [offCode, setOffCode] = useState('');

  const start = useMutation({
    mutationFn: () => api('POST /api/auth/2fa/setup'),
    onSuccess: async (r) => setSetup({ ...r, qr: await QRCode.toDataURL(r.otpauthUrl, { margin: 1, width: 200 }) }),
    onError: (e) => toast.error(e),
  });
  const enable = useMutation({
    mutationFn: () => api('POST /api/auth/2fa/enable', { body: { code } }),
    onSuccess: (r) => {
      setRecovery(r.recoveryCodes);
      setSetup(null);
      setCode('');
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.sessions });
    },
  });
  const disable = useMutation({
    mutationFn: () => api('POST /api/auth/2fa/disable', { body: { password: offPassword, code: offCode } }),
    onSuccess: () => {
      toast.success('Two-step sign-in is off.');
      setOffPassword('');
      setOffCode('');
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
  useEffect(() => () => setSetup(null), []);
  if (!me) return null;
  const hasPassword = me.user.authProviders.includes('password');
  const on = me.user.twoFactorEnabled;

  const copyCodes = () => void copyText(recovery!.join('\n')).then(() => toast.success('Recovery codes copied.')).catch(() => toast.error('Could not copy — download your recovery codes instead.'));
  const downloadCodes = () => {
    const blob = new Blob([`MailPilot recovery codes for ${me.user.email}\nEach code works once.\n\n${recovery!.join('\n')}\n`], { type: 'text/plain' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'mailpilot-recovery-codes.txt' });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card>
      <CardHeader
        icon={<ShieldCheck className="h-5 w-5" />}
        title="Two-step sign-in"
        description="When you sign in with your password, also enter a code from an authenticator app on your phone."
        actions={on ? <Badge tone="success">On</Badge> : undefined}
      />
      <CardBody className="space-y-4">
        {!hasPassword ? (
          <Notice tone="info">You sign in with Google or Microsoft, which protect your account with their own 2-step verification. Turn it on in your Google or Microsoft account settings.</Notice>
        ) : recovery ? (
          <div className="space-y-3">
            <Notice tone="success">Two-step sign-in is on. Other devices were signed out and will ask for a code next time.</Notice>
            <p className="text-[0.8125rem] text-ink">
              <strong>Save these recovery codes.</strong> If you lose your phone, each one lets you sign in once. They won't be shown again.
            </p>
            <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-sunken p-3 font-mono text-[0.8125rem] text-ink sm:grid-cols-5">
              {recovery.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" icon={<Copy className="h-4 w-4" />} onClick={copyCodes}>Copy</Button>
              <Button size="sm" variant="outline" icon={<Download className="h-4 w-4" />} onClick={downloadCodes}>Download</Button>
              <Button size="sm" variant="primary" onClick={() => setRecovery(null)}>I've saved them</Button>
            </div>
          </div>
        ) : on ? (
          <form
            className="max-w-sm space-y-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              disable.mutate();
            }}
          >
            <p className="text-[0.8125rem] text-muted">To turn it off, confirm your password and a current code (or a recovery code).</p>
            <Field label="Password">{(id) => <Input id={id} type="password" autoComplete="current-password" value={offPassword} onChange={(e) => setOffPassword(e.target.value)} />}</Field>
            <Field label="Code">{(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" value={offCode} onChange={(e) => setOffCode(e.target.value)} placeholder="123456" />}</Field>
            <InlineError error={disable.error} />
            <Button type="submit" variant="danger" size="sm" loading={disable.isPending} disabled={!offPassword || !offCode}>Turn off two-step sign-in</Button>
          </form>
        ) : setup ? (
          <form
            className="space-y-4"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              enable.mutate();
            }}
          >
            <ol className="list-decimal space-y-1 pl-5 text-[0.8125rem] text-ink">
              <li>Open an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).</li>
              <li>Scan this QR code, or enter the key by hand.</li>
              <li>Type the 6-digit code it shows.</li>
            </ol>
            <div className="flex flex-wrap items-center gap-5">
              <img src={setup.qr} alt="QR code for your authenticator app" width={180} height={180} className="rounded-xl bg-white p-2 shadow-card" />
              <div className="min-w-0 space-y-1">
                <p className="text-xs text-muted">Key (if you can't scan)</p>
                <p className="break-all font-mono text-[0.8125rem] tracking-wider text-ink">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</p>
              </div>
            </div>
            <Field label="6-digit code" className="max-w-[12rem]">
              {(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-autofocus />}
            </Field>
            <InlineError error={enable.error} />
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" loading={enable.isPending} disabled={code.replace(/\s/g, '').length !== 6}>Turn on</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setSetup(null)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <Button variant="primary" size="sm" icon={<Smartphone className="h-4 w-4" />} loading={start.isPending} onClick={() => start.mutate()}>Set up two-step sign-in</Button>
        )}
      </CardBody>
    </Card>
  );
}
